import React, { useEffect } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useAppSelector } from 'app/store';
import { selectAuth } from 'app/store/authSlice';
import AuthSplitLayout from 'app/pages/auth/shared/AuthSplitLayout';
import AuthButton from 'app/pages/auth/shared/AuthButton';
import { authColors, authFontFamily, authType } from 'app/pages/auth/shared/authTheme';
import useCreatePasskey from 'app/auth/useCreatePasskey';
import {
  recordPasskeyCreated,
  shouldShowPasskeyPrompt,
  stopAskingPasskey,
} from 'app/auth/passkeyPromptChoice';
import { passkeyPromptShownKeyFor } from 'app/auth/passkeyPromptSession';

/**
 * Where users can read about passkeys (from the "Learn how passkeys work"
 * link). Points at the FIDO Alliance's consumer explainer until marketing
 * ships a page at https://www.speedchain.com/passkeys — swap back then, never
 * ship a 404.
 */
const PASSKEY_HELP_URL = 'https://fidoalliance.org/passkeys/';

const Body = styled(Typography)({
  ...authType.body,
  fontFamily: authFontFamily,
  color: authColors.textMuted,
  textAlign: 'center',
  width: '100%',
});

const InlineLink = styled('a')({
  ...authType.body,
  fontFamily: authFontFamily,
  color: authColors.textMuted,
  textDecoration: 'underline',
});

const ErrorText = styled(Typography)({
  ...authType.helper,
  fontFamily: authFontFamily,
  color: authColors.error,
  textAlign: 'center',
  width: '100%',
});

const DontAskAgain = styled('button')({
  background: 'none',
  border: 'none',
  padding: 4,
  cursor: 'pointer',
  alignSelf: 'center',
  ...authType.helper,
  fontFamily: authFontFamily,
  color: authColors.textMuted,
  textDecoration: 'underline',
});

const Reassure = styled(Typography)({
  ...authType.helper,
  fontFamily: authFontFamily,
  color: authColors.textMuted,
  textAlign: 'center',
  width: '100%',
});

/**
 * SEE-213 post-login prompt: "Sign in faster next time!" (Figma 4230:34346).
 * Create → SMS step-up (if the session isn't MFA-fresh) → native passkey sheet
 * → success toast → dashboard. A cancelled sheet keeps the user here; a
 * failure keeps the user here with the error shown, so they can retry or leave
 * via "Not now".
 *
 * Passkeys are never mandatory. Three dismiss paths, none of which ever block
 * the user from reaching the dashboard:
 *   - "Not now": soft dismiss for this login session only; we ask again on the
 *     next login (SEE-1146 — no decline cap, "Not now" never silences the
 *     prompt for good).
 *   - "Don't ask again": hard dismiss; never prompt on this device again.
 *   - cancel/close the native sheet: stay on this screen, no choice recorded.
 * The Figma "N/3 opt outs remaining" counter is deliberately NOT rendered — it
 * read like a login limit and confused users. Instead we reassure that a
 * passkey can be set up later, and give an explicit opt-out.
 */
export default function PasskeyPrompt() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAppSelector(selectAuth);
  const sub = user?.sub ?? '';
  const { create, modal, creating, error } = useCreatePasskey();

  // Where to go after any choice: the deep-link the user was headed to before
  // login (forwarded by Login), else the normal landing router.
  const next = (location.state as { from?: string } | null)?.from ?? '/landing';

  // Self-guard for direct/bookmark/reload access: if this user shouldn't be
  // prompted (already has a passkey, dismissed the prompt, or an SSO-linked account),
  // don't show it — continue on. The normal entry (Login/Landing) already
  // checks this; this just makes the page safe to hit directly.
  const eligible = Boolean(sub) && !user?.identities && shouldShowPasskeyPrompt(sub);

  // Loop guard for Landing's redirect, per user, marked on mount (not during
  // Landing's render): this session has shown the prompt once.
  useEffect(() => {
    if (!eligible) return;
    try {
      sessionStorage.setItem(passkeyPromptShownKeyFor(sub), '1');
    } catch {
      /* storage unavailable — worst case the prompt can show again */
    }
  }, [sub, eligible]);

  if (!eligible) {
    return <Navigate to={next} replace />;
  }

  const finish = () => navigate(next, { replace: true });

  const onCreate = () => {
    create((outcome) => {
      if (outcome.created) {
        recordPasskeyCreated(sub);
        enqueueSnackbar(t('passkeyCreatedToast'), { variant: 'success' });
        finish();
      } else if (outcome.alreadyRegistered) {
        // This authenticator already holds a passkey for the account: the user
        // is enrolled — remember that and stop prompting.
        recordPasskeyCreated(sub);
        enqueueSnackbar(t('passkeyAlreadyOnDevice'), { variant: 'info' });
        finish();
      }
      // cancelled / failed: stay — the inline error (if any) explains, and the
      // user can retry or leave via "Not now".
    });
  };

  // "Not now" records nothing: the session guard set on mount already keeps
  // the prompt away for the rest of this login session, and the next login
  // asks again.
  const onNotNow = () => {
    finish();
  };

  const onDontAskAgain = () => {
    stopAskingPasskey(sub);
    finish();
  };

  return (
    <AuthSplitLayout title={t('passkeyPromptTitle')}>
      <Body>
        {t('passkeyPromptBody')}{' '}
        <InlineLink href={PASSKEY_HELP_URL} target="_blank" rel="noopener noreferrer">
          {t('passkeyLearnHow')}
        </InlineLink>
      </Body>

      {error && <ErrorText role="alert">{t('passkeyCreateFailed')}</ErrorText>}

      <Box sx={{ display: 'flex', gap: '12px', width: '100%', px: '24px' }}>
        <Box sx={{ flex: '1 0 0', minWidth: 0 }}>
          <AuthButton variant="outlined" onClick={onNotNow} disabled={creating}>
            {t('notNow')}
          </AuthButton>
        </Box>
        <Box sx={{ flex: '1 0 0', minWidth: 0 }}>
          <AuthButton variant="filled" onClick={onCreate} loading={creating}>
            {t('createPasskey')}
          </AuthButton>
        </Box>
      </Box>

      <Reassure>{t('passkeySetUpLater')}</Reassure>
      <DontAskAgain type="button" onClick={onDontAskAgain} disabled={creating}>
        {t('passkeyDontAskAgain')}
      </DontAskAgain>

      {modal}
    </AuthSplitLayout>
  );
}
